import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon/Icon";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import signOut from "@ucs/Handlers/UcsSignOut";
import { Roles } from "@ucs/Helpers/Enums";
import Checkmark from "@ucs/Icons/Checkmark";
import ChevronDown from "@ucs/Icons/ChevronDownLarge";
import ChevronUp from "@ucs/Icons/ChevronUp";
import Close from "@ucs/Icons/Close";
import UCSColors from "@ucs/Utils/UCSColors";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    isAuthenticated: state.context.session!.isAuthenticated,
    isAccounting: state.context.session!.userRoles ? state.context.session!.userRoles.includes(Roles.Accounting) : false,
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};


const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    links = "links",
}
interface OwnProps extends WidgetProps {
}


const Container = styled.div`
    position: relative;
`;

const Input = styled.div`
    background: white;
    border: 2px solid ${UCSColors.gray07};
    padding: 8px 16px;
    padding-right: 30px;
    font-weight: 700;
    font-size: 18px;
    color: ${UCSColors.gray03};
    width: 280px;
    position: relative;
    ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                width: 100%;
            `,
        ]);
    }}
`;

const Shader = styled.div`
    display: block;
    position: fixed;
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0px;
    z-index: 98;
    ${({ theme }) => {
        const mobileStyling = css`
            background: rgba(0, 0, 0, 0.5);
        `;
        return breakpointMediaQueries(theme, [mobileStyling, mobileStyling]);
    }};
`;

const Dropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0px;
    right: 0px;
    max-height: 400px;
    width: 280px;
    overflow-y: auto;
    background-color: white;
    z-index: 99;
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.25);

    a {
        display: block;
    }

    ${({ theme }) => {
        const mobileStyling = css`
            position: fixed;
            width: auto;
            left: 50px;
            right: 50px;
            bottom: 50px;
            top: 50px;
            margin: auto;
            padding-top: 26px;
        `;
        return breakpointMediaQueries(theme, [mobileStyling, mobileStyling]);
    }};
`;

const DropdownItem = styled.div`
    padding: 11px 32px;
    font-size: 18px;
    color: ${UCSColors.gray03};
    position: relative;
`;

const DropdownGroup = styled.div`
    border-top: 1px solid ${UCSColors.gray07};
    border-bottom: 1px solid ${UCSColors.gray07};
`;

const MyAccount = styled.div`
border-top: none;
border-bottom: none;
`;

const CloseIcon = styled.div`
    display: none;
    position: absolute;
    ${({ theme }) => {
        const mobileStyling = css`
            display: block;
            top: 10px;
            right: 10px;
        `;
        return breakpointMediaQueries(theme, [mobileStyling, mobileStyling]);
    }};
`;

const ChevronIcon = styled.div`
    position: absolute;
    right: 16px;
    top: 0px;
    height: 24px;
    bottom: 0px;
    margin: auto;
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;
let icon: React.ComponentType;
const AccountDropdown = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    isAccounting,
    isAuthenticated,
}: Props) => {
    const [isOpen, setIsOpen] = React.useState(false);
    let currentPage = myAccountPageLink?.url === currentLocation.pathname ? myAccountPageLink.title : "";
    const onSignInHandler = () => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}`);
                }
            }
        }
    };

    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Login/Register");
    if (userName && !currentUserIsGuest) {
        signInStatusText = translate("My Account");
    }

    const menuItems: any[] = Object.assign([], myAccountPageLink?.children ?? []);

    if(isAuthenticated){
        menuItems.push({
            title: "Log Out",
            clickableProps: {
                onClick: onSignOutHandler,
            },
        });
    }
    
    
    const dropdownItems: any[] = [];

    if (myAccountPageLink) {
        dropdownItems.push(
            <Link href={myAccountPageLink.url}>
                <MyAccount>
                    {myAccountPageLink.url === currentLocation.pathname && (
                        <Icon
                            src={Checkmark}
                            color="primary"
                            size={15}
                            style={{ position: "absolute", left: "6px", top: "0px", bottom: "0px", margin: "auto" }}
                        />
                    )}
                    <Typography
                            weight={700}
                            size={13}
                            color={UCSColors.gray04}
                            style={{ 
                                left: "6px",
                                top: "0px",
                                bottom: "0px",
                                margin: "auto",
                                padding: "8px 16px",
                                display: "block"
                                
                            }}
                            >
                        {myAccountPageLink.title}
                    </Typography>
                </MyAccount>
            </Link>,
        );
    }

    if (myAccountPageLink?.children?.filter(item => !item.excludeFromNavigation)) {
        myAccountPageLink?.children?.filter(item => !item.excludeFromNavigation)?.forEach(child => {
            if (currentLocation.pathname === child.url) {
                currentPage = child.title;
            }
            const hideQuoteAndJobsPage = isAccounting && child.title === "Quotes";
            const childrenWithoutExclusionDoesNotExists= !child.children ||   child.children?.length===0;
            const children=child.children?.filter(item => !item.excludeFromNavigation);
            if (children && children?.length > 0 && child.title !== "Unused Pages" && !hideQuoteAndJobsPage) {
                const childDropdownItems: any[] = [];

                child.children?.filter(item => !item.excludeFromNavigation)?.forEach(grandChild => {
                    if (currentLocation.pathname === grandChild.url) {
                        currentPage = grandChild.title;
                    }
                    
                    let openInNewTab = false;  
                    if(child.title.toLocaleLowerCase().includes("invoice") && fields?.openInvoiceInNewTab){
                        openInNewTab = true;
                    }
                    childDropdownItems.push(
                        <Link href={grandChild.url} target={openInNewTab ? "_blank" : ""}>
                            <DropdownItem>
                                {grandChild.url === currentLocation.pathname && (
                                    <Icon
                                        src={Checkmark}
                                        color="primary"
                                        size={15}
                                        style={{
                                            position: "absolute",
                                            left: "10px",
                                            top: "0px",
                                            bottom: "0px",
                                            margin: "auto",
                                        }}
                                    />
                                )}
                                <Typography weight={grandChild.url === currentLocation.pathname ? 500 : 400}>
                                    {grandChild.title}
                                </Typography>
                            </DropdownItem>
                        </Link>,
                    );
                });
                if (childDropdownItems.length > 0) {
                    dropdownItems.push(
                        <DropdownGroup>
                            <Typography
                                weight={700}
                                size={13}
                                color={UCSColors.gray04}
                                style={{ padding: "8px 16px", display: "block" }}
                            >
                                {child.title}
                            </Typography>
                            {childDropdownItems}
                        </DropdownGroup>,
                    );
                }
            } else if(childrenWithoutExclusionDoesNotExists && child.title !== "Unused Pages" && !hideQuoteAndJobsPage) {
                dropdownItems.push(
                    <Link href={child.url}>
                        <DropdownItem>
                            {child.url === currentLocation.pathname && (
                                <Icon
                                    src={Checkmark}
                                    color="primary"
                                    size={15}
                                    style={{
                                        position: "absolute",
                                        left: "6px",
                                        top: "0px",
                                        bottom: "0px",
                                        margin: "auto",
                                    }}
                                />
                            )}
                            <Typography weight={child.url === currentLocation.pathname ? 500 : 400}>
                                {child.title}
                            </Typography>
                        </DropdownItem>
                    </Link>,
                );
                // }
            }
        });
    }

    return (
      <Container>
          {isOpen && (<Shader onClick={() => setIsOpen(false)}></Shader>)}
          <Input onClick={() => setIsOpen(!isOpen)}>
            {currentPage}
            <ChevronIcon>
                <Icon src={isOpen ? ChevronUp : ChevronDown} color={UCSColors.gray04} size={16}></Icon>
            </ChevronIcon>
          </Input>
          {isOpen && (<Dropdown>
            <CloseIcon onClick={() => setIsOpen(false)}>
                <Icon src={Close} size={16} color="black"></Icon>
            </CloseIcon>
              {dropdownItems}
              { isAuthenticated && <DropdownItem onClick={onSignOutHandler}>
                    Log Out
              </DropdownItem>}
          </Dropdown>)}
      </Container>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(AccountDropdown)),
    definition: {
        displayName: "Account Dropdown",
        icon: "User",
        group: "Common",
        fieldDefinitions: [
            {
                name: "openInvoiceInNewTab",
                displayName: "Open Invoice in New Tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ]
    },
};

export default widgetModule;
